import React from 'react'
import { motion } from 'motion/react'
import { Gem } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { parseSkinName } from '../../../utils/formatters'
import { getContractProfit } from '../services/contractGameApi'

const STARS = Array.from({ length: 32 }, (_, index) => ({
  x: (index * 73 + 29) % 390 + 5,
  y: (index * 137 + 53) % 390 + 5,
  radius: index % 7 === 0 ? 1.4 : 0.65,
  opacity: index % 3 === 0 ? 0.55 : 0.25
}))

const SupernovaArtwork = () => {
  const gradientId = 'contract-supernova-gradient'
  const glowId = 'contract-supernova-glow'
  return (
    <svg className="pgv__art contract-supernova__art" viewBox="0 0 400 400" fill="none" aria-hidden="true">
      <defs>
        <linearGradient id={gradientId} x1="40" y1="50" x2="340" y2="340" gradientUnits="userSpaceOnUse">
          <stop stopColor="var(--portal-secondary)" stopOpacity="0.12" />
          <stop offset="0.46" stopColor="var(--portal-primary)" />
          <stop offset="0.75" stopColor="white" stopOpacity="0.86" />
          <stop offset="1" stopColor="var(--portal-secondary)" stopOpacity="0.18" />
        </linearGradient>
        <radialGradient id={glowId}>
          <stop stopColor="var(--portal-primary)" stopOpacity="0.22" />
          <stop offset="0.55" stopColor="var(--portal-primary)" stopOpacity="0.065" />
          <stop offset="1" stopColor="var(--portal-primary)" stopOpacity="0" />
        </radialGradient>
      </defs>
      {STARS.map((star, index) => <circle key={index} cx={star.x} cy={star.y} r={star.radius} fill="white" opacity={star.opacity} />)}
      <circle cx="200" cy="200" r="190" fill={`url(#${glowId})`} className="pgv__atmosphere" />
      <g className="pgv__shock"><circle cx="200" cy="200" r="78" stroke={`url(#${gradientId})`} strokeWidth="2" /></g>
      <g className="contract-supernova__outer-spin">
        <g className="pgv__outer">
          <circle cx="200" cy="200" r="158" stroke="var(--portal-primary)" strokeOpacity="0.12" strokeWidth="0.7" strokeDasharray="2 9" />
          <circle cx="200" cy="200" r="139" stroke={`url(#${gradientId})`} strokeOpacity="0.63" strokeWidth="1.5" strokeDasharray="174 27 66 48 120 440" />
          <circle cx="200" cy="200" r="130" stroke="var(--portal-primary)" strokeOpacity="0.32" strokeWidth="0.6" strokeDasharray="1 7" />
        </g>
      </g>
      <g className="contract-supernova__inner-spin">
        <g className="pgv__inner">
          <circle cx="200" cy="200" r="106" stroke={`url(#${gradientId})`} strokeWidth="3" strokeDasharray="120 22 46 28 125 325" />
          <circle cx="200" cy="200" r="100" stroke="var(--portal-secondary)" strokeOpacity="0.28" strokeWidth="0.8" />
          <circle cx="200" cy="200" r="90" stroke="var(--portal-primary)" strokeOpacity="0.4" strokeWidth="1" strokeDasharray="2 12" />
        </g>
      </g>
      <g className="pgv__feature pgv__feature--supernova">
        {Array.from({ length: 18 }, (_, index) => <path key={index} d="M200 48 L200 71 M200 82 L200 91" transform={`rotate(${index * 20} 200 200)`} stroke={`url(#${gradientId})`} strokeWidth={index % 3 === 0 ? 2 : 0.8} />)}
        <circle cx="200" cy="200" r="120" stroke={`url(#${gradientId})`} strokeWidth="8" strokeOpacity="0.35" strokeDasharray="3 17" />
        <path d="M200 84 L209 166 L316 200 L208 211 L200 318 L190 211 L84 200 L191 190 Z" stroke="var(--portal-primary)" strokeOpacity="0.32" strokeWidth="0.8" />
      </g>
      <g className="pgv__core">
        <circle cx="200" cy="200" r="73" fill="#020309" stroke={`url(#${gradientId})`} strokeWidth="1.5" />
        <circle cx="200" cy="200" r="67" stroke="var(--portal-primary)" strokeOpacity="0.25" strokeWidth="0.6" />
        <circle cx="200" cy="200" r="57" fill={`url(#${glowId})`} />
        <circle cx="200" cy="200" r="44" fill="#020309" />
        <circle cx="200" cy="200" r="36" stroke="var(--portal-primary)" strokeOpacity="0.25" strokeWidth="0.8" strokeDasharray="2 7" />
      </g>
      <g className="pgv__center-mark" stroke={`url(#${gradientId})`} strokeWidth="1.2">
        <circle cx="200" cy="200" r="25" />
        <circle cx="200" cy="200" r="16" />
        <path d="M200 165 V178 M200 222 V235 M165 200 H178 M222 200 H235" />
        <circle cx="200" cy="200" r="2" fill="var(--portal-primary)" stroke="none" />
      </g>
    </svg>
  )
}

const ContractSupernova = ({ items, phase, result, round, reducedMotion, onRemove }) => {
  const { t, i18n } = useTranslation()
  const signed = phase === 'result' && result?.win
  const { title, subtitleCompact } = parseSkinName(result?.win?.name)
  const price = Number(result?.win?.price ?? result?.win?.cost ?? 0)
  const profit = getContractProfit(result)
  return (
    <div className="pgv__stage contract-supernova__stage" data-phase={phase === 'request' ? 'idle' : phase}>
      <div className="pgv__scene">
        <SupernovaArtwork />
        <div className="pgv__tokens contract-supernova__tokens" aria-hidden={phase !== 'ready' || !onRemove}>
          {items.map((item, index) => <span key={item.id} className="pgv__orbit" style={{ '--angle': `${-90 + index * 360 / items.length}deg` }}><button type="button" className="pgv__token" style={{ '--skin-rgb': '255, 166, 93' }} disabled={phase !== 'ready' || !onRemove} onClick={() => onRemove?.(item.id)} aria-label={t('contractNew.removeSkin', { name: item.name })} title={phase === 'ready' && onRemove ? item.name : undefined}><span className="contract-supernova__skin">{item.img ? <img src={item.img} alt="" className="h-9 w-9 object-contain" /> : <Gem size={21} />}</span></button></span>)}
        </div>
        <div className="pgv__flash" />
        {signed && <div className="contract-supernova__result-wrap">
          <motion.div key={round} initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: reducedMotion ? 0 : 0.55, ease: [0.16, 1, 0.3, 1] }} className="contract-supernova__result">
            <div className="contract-supernova__result-skin">{result.win.img ? <img src={result.win.img} alt="" /> : <Gem size={27} />}</div>
            <strong className="contract-supernova__result-title" title={result.win.name}>{title}</strong>
            {subtitleCompact && <span className="contract-supernova__result-variant" title={subtitleCompact}>{subtitleCompact}</span>}
            <span className="contract-supernova__result-price"><Gem size={12} />{price.toLocaleString(i18n.language, { numberingSystem: 'latn' })}</span>
            {profit !== null && <span className={`contract-supernova__result-profit ${profit > 0 ? 'text-accent-primary' : profit < 0 ? 'text-error' : 'text-white'}`}>{profit > 0 ? '+' : ''}{Number(profit.toFixed(1)).toLocaleString(i18n.language, { numberingSystem: 'latn' })}%</span>}
          </motion.div>
        </div>}
      </div>
      <span className="sr-only" role="status">{phase === 'playing' ? t('contractNew.animating') : signed ? t('contractNew.resultStatus', { name: result.win.name }) : ''}</span>
    </div>
  )
}

export default ContractSupernova
